/**
 * Chutt... — Cache Freshness Service
 * Detects when CRON has produced newer data than what's in localStorage.
 * Invalidates all brand caches so pages re-fetch from the fresh backend.
 */
import axios from 'axios';
import { invalidateBrandCache } from './cache';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// All localStorage cache key prefixes per brand
const BRAND_CACHE_PREFIXES = [
  'radar_data_',
  'radar_v1_',
  'comprendre_data_',
  'diag_cache_',
  'mesurer_data_',
  'mesurer_dashboard_',
  'agir_actions_',
  'expert_rp_cache_',
  'serp_cache_',
  'influence_data_',
];

/**
 * Fetch data_updated_at timestamps for all brands from the backend.
 * Returns { brandId: "ISO timestamp" | null }
 */
export async function fetchFreshness() {
  const token = localStorage.getItem('token');
  if (!token) return {};

  try {
    const res = await axios.get(`${API_URL}/api/v2/freshness`, {
      headers: { Authorization: `Bearer ${token}` },
      timeout: 5000, // 5s max — this must be fast
    });
    return res.data || {};
  } catch (err) {
    // Silent fail — fallback to existing TTL-based cache
    return {};
  }
}

/**
 * Get the stored freshness timestamp for a brand from localStorage.
 */
export function getStoredFreshness(brandId) {
  try {
    return localStorage.getItem(`chutt_freshness_${brandId}`) || null;
  } catch (_) {
    return null;
  }
}

/**
 * Save the server's freshness timestamp for a brand.
 */
export function setStoredFreshness(brandId, isoTimestamp) {
  try {
    localStorage.setItem(`chutt_freshness_${brandId}`, isoTimestamp);
  } catch (_) { /* localStorage full — ignore */ }
}

/**
 * Check if the server has newer data than our stored freshness.
 */
export function shouldInvalidate(brandId, freshnessMap) {
  const serverTs = freshnessMap[String(brandId)];
  if (!serverTs) return false; // No server timestamp → don't invalidate

  const localTs = getStoredFreshness(brandId);
  if (!localTs) return false; // Never synced → just store timestamp, don't wipe existing caches

  return serverTs > localTs; // Server is newer → invalidate
}

/**
 * Invalidate ALL localStorage + in-memory caches for a brand.
 */
export function invalidateAllBrandCaches(brandId) {
  // Clear all localStorage keys for this brand
  for (const prefix of BRAND_CACHE_PREFIXES) {
    try {
      localStorage.removeItem(`${prefix}${brandId}`);
    } catch (_) { /* ignore */ }
  }

  // Clear in-memory cache (cache.js Map)
  invalidateBrandCache(brandId);
}

/**
 * Full freshness check for a brand: fetch, compare, invalidate if stale.
 * Returns { invalidated: boolean, lastUpdated: Date | null }
 */
export async function checkAndSync(brandId) {
  const freshnessMap = await fetchFreshness();
  const serverTs = freshnessMap[String(brandId)];

  if (shouldInvalidate(brandId, freshnessMap)) {
    invalidateAllBrandCaches(brandId);
    setStoredFreshness(brandId, serverTs);
    return { invalidated: true, lastUpdated: serverTs ? new Date(serverTs) : null };
  }

  // Update stored freshness even if no invalidation needed (first sync)
  if (serverTs) {
    setStoredFreshness(brandId, serverTs);
  }

  return { invalidated: false, lastUpdated: serverTs ? new Date(serverTs) : null };
}
